/** @jsx jsx */
import { jsx, ThemeProvider } from '@emotion/react'
import { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import mq from '../styles/mediaQueries'
import { Link } from 'gatsby'

import TwitterSVG from '../assets/icons/twitter.svg'
import GithubSVG from '../assets/icons/github.svg'
import SolidusSVG from '../assets/logo/solidus-logo.svg'

import Contact from './Contact'

const dayTheme = {
  listColor: 'var(--terminal-500)',
  subFooterColor: 'var(--terminal-300)',
  borderColor: 'var(--terminal-100)',
}

const nightTheme = {
  listColor: 'var(--terminal-0)',
  subFooterColor: 'var(--terminal-200)',
  borderColor: 'var(--terminal-400)',
}

const greyTheme = {
  listColor: 'var(--terminal-500)',
  subFooterColor: 'var(--terminal-300)',
  borderColor: 'var(--terminal-100)',
}

const FooterTwitter = props => {
  const StyledTwitter = styled(TwitterSVG)`
    pointer-events: auto;
    cursor: pointer;
    margin-right: 16px;
    height: 24px;
    width: 24px;

    path {
      fill: ${props => props.theme.listColor};
    }
  `

  return (
    <a href="https://twitter.com/supergoodsoft">
      <StyledTwitter {...props} />
    </a>
  )
}

const FooterGithub = props => {
  const StyledGithub = styled(GithubSVG)`
    pointer-events: auto;
    cursor: pointer;
    margin-right: 16px;
    height: 24px;
    width: 24px;

    path {
      fill: ${props => props.theme.listColor};
    }
  `

  return (
    <a href="https://github.com/SuperGoodSoft">
      <StyledGithub {...props} />
    </a>
  )
}

const FooterSolidus = props => {
  const StyledSolidus = styled(SolidusSVG)`
    pointer-events: auto;
    cursor: pointer;
    height: 24px;
    margin-left: auto;
    display: block;

    path {
      fill: ${props => props.theme.listColor};
    }
  `

  const StyledLink = styled.a`
    margin-left: auto;
  `

  return (
    <StyledLink href="https://solidus.io">
      <StyledSolidus {...props} />
    </StyledLink>
  )
}

const FooterContactWrapper = styled.div`
  position: absolute;
  ${mq({
    top: ['-36px', '-36px', '-48px', '-48px', '-48px'],
    right: ['12px', '12px', '48px', '48px', '48px'],
  })};
`

const FooterWrapper = styled.div`
  ${mq({
    gridColumnStart: ['1', '1', '2', '2', '2'],
    gridColumnEnd: ['5', '9', '12', '12', '12'],
    marginTop: ['160px', '160px', '160px', '200px', '200px'],
  })};
  margin-bottom: 24px;
  position: relative;
  border-top: 1px solid;
  border-left: none;
  border-right: none;
  border-bottom: none;
  border-color: ${props => props.theme.borderColor};
`

const FooterList = styled.div`
  ${mq({
    padding: ['12px 0', '12px 0', '24px 0', '24px 0', '24px 0'],
  })};
  display: flex;
  flex-direction: column;
  margin: 24px 0;
  color: ${props => props.theme.listColor};
`

const FooterHorizontalLine = styled.hr`
  width: 100%;
  border-top: 1px solid;
  border-left: none;
  border-right: none;
  border-bottom: none;
  border-color: ${props => props.theme.borderColor};
  padding-inline-start: 0;
  margin-block-start: 0;
  margin-block-end: 0;
`

const FooterLink = styled(Link)`
  text-decoration: none;
  font-family: degular-display, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 28px;
  line-height: 142.86%;
  letter-spacing: 0.015em;
  color: inherit;
`

const FooterSocial = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-inline-start: 0;
  margin-block-start: 0;
  margin-block-end: 0;
  margin: 24px 0;
`

const SubFooter = styled.div`
  ${mq({
    flexDirection: ['column', 'column', 'row', 'row', 'row'],
    margin: ['24px 0 12px 0', '24px 0 12px 0', '24px 0', '24px 0', '24px 0'],
  })};
  display: flex;
  align-items: flex-start;
  color: ${props => props.theme.subFooterColor};
`

const SubFooterLinks = styled(Link)`
  ${mq({
    marginRight: ['0', '0', '24px', '24px', '24px'],
    marginBottom: ['12px', '12px', '0', '0', '0'],
  })};
  font-family: degular, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 133.3%;
  text-align: right;
  letter-spacing: 0.02em;
  color: inherit;
  text-decoration: none;
`

const SubFooterText = styled.span`
  ${mq({
    marginRight: ['0', '0', '24px', '24px', '24px'],
    marginBottom: ['12px', '12px', '0', '0', '0'],
  })};
  font-family: degular, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 133.3%;
  text-align: right;
  letter-spacing: 0.02em;
  color: inherit;
  text-decoration: none;
`

function Footer({ color, copyrightYear }) {
  const [theme, setTheme] = useState(dayTheme)
  const [contactTheme, setContactTheme] = useState('white')

  useEffect(() => {
    if (color === 'dark') {
      setTheme(nightTheme)
      setContactTheme('dark')
    } else if (color === 'grey') {
      setTheme(greyTheme)
      setContactTheme('grey')
    } else {
      setTheme(dayTheme)
      setContactTheme('white')
    }
  }, [color])

  return (
    <ThemeProvider theme={theme}>
      <FooterWrapper>
        <FooterList>
          <FooterLink to="/work">Work</FooterLink>
          <FooterLink to="/services"> Services</FooterLink>
          <FooterLink to="/about">About</FooterLink>
          <FooterLink to="/blog">Blog</FooterLink>
          <FooterLink to="/careers">Careers</FooterLink>
          <FooterLink to="/contact">Contact</FooterLink>
        </FooterList>
        <FooterHorizontalLine />
        <FooterSocial>
          <FooterGithub />
          <FooterTwitter />
          <FooterSolidus />
        </FooterSocial>
        <FooterHorizontalLine />
        <SubFooter>
          <SubFooterLinks to="/privacy">Privacy policy</SubFooterLinks>
          <SubFooterLinks to="/toc">Terms and conditions</SubFooterLinks>
          <SubFooterText>
            © {copyrightYear} Super Good Software Inc. All rights reserved.
          </SubFooterText>
        </SubFooter>
        <FooterContactWrapper>
          <Contact colorTheme={contactTheme} />
        </FooterContactWrapper>
      </FooterWrapper>
    </ThemeProvider>
  )
}

export default Footer
